/**
 * Coalesce a burst of "something changed, re-read" signals into O(1) reads.
 *
 * A realtime invalidation can arrive once per changed ROW: one approval that
 * writes N notifications broadcasts N times, and each broadcast used to start
 * its own read. In production one session issued ~1,400 unread-count reads in
 * a single minute that way — enough load that the realtime channel itself
 * went quiet afterwards.
 *
 * `trigger()` schedules `run` on a trailing debounce (`wait` ms after the last
 * signal), but never later than `maxWait` ms after the first signal of a
 * burst, so a continuous stream still refreshes. At most one `run` is in
 * flight: a signal that arrives while it is running schedules exactly one more
 * run after it settles, so the final state is always read after the burst.
 */
export interface CoalescedRunner {
  /** Signal a change; the read happens later, coalesced with other signals. */
  trigger: () => void;
  /** Drop any pending run and ignore results of the one in flight. */
  cancel: () => void;
}

export function createCoalescedRunner(
  run: () => Promise<unknown>,
  { wait = 1000, maxWait = 3000 }: { wait?: number; maxWait?: number } = {},
): CoalescedRunner {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let burstStartedAt: number | null = null;
  let inFlight = false;
  let rerun = false;
  let cancelled = false;

  const fire = () => {
    timer = null;
    burstStartedAt = null;
    if (cancelled) return;
    if (inFlight) {
      rerun = true;
      return;
    }
    inFlight = true;
    void Promise.resolve()
      .then(run)
      .catch(() => {
        // The caller's `run` owns error reporting; a failure must not stop
        // later signals from refreshing.
      })
      .finally(() => {
        inFlight = false;
        if (rerun && !cancelled) {
          rerun = false;
          schedule();
        }
      });
  };

  const schedule = () => {
    if (cancelled) return;
    const now = Date.now();
    if (burstStartedAt === null) burstStartedAt = now;
    const untilMax = burstStartedAt + maxWait - now;
    const delay = Math.max(0, Math.min(wait, untilMax));
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(fire, delay);
  };

  return {
    trigger: schedule,
    cancel: () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
      timer = null;
    },
  };
}
